import { OfficeFinishing } from "@/api/offices";
import { S3_URL } from "@/api/s3";
import { useFileUploadModal } from "@/app/components/fileUpload/useFileUploadModal";
import { NewLocationHandbook } from "@/app/components/handbooks/locationHandbook";
import { CodependentLockHandbook } from "@/app/components/handbooks/lockHandbook";
import { NewOfficeTypeHandbook } from "@/app/components/handbooks/officeTypeHandbook";
import { parseDate } from "@internationalized/date";
import {
  Autocomplete,
  AutocompleteItem,
  Button,
  DatePicker,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Textarea,
  Checkbox,
  Tooltip,
} from "@heroui/react";
import { I18nProvider } from "@react-aria/i18n";
import { Controller, useForm } from "react-hook-form";
import { FaUpload } from "react-icons/fa6";
import { FaTimes as FaTimesIcon } from "react-icons/fa";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "@/app/components/sortableItem";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDisclosure } from "@heroui/react";
import { ModalError } from "../modals/modalBuilder";
import { api } from "@/api";
import { toast } from "react-toastify";
import { useCianModal } from "../modals/cian/useCianModal";
import { normalizeDate } from "@/utils";

interface OfficeFormProps {
  defaultValues?: Partial<OfficeFormData>;
  onSubmit: (data: OfficeFormData) => void;
  onDelete?: () => void;
  isEdit?: boolean;
  modalErrors?: ModalError[];
  officeId?: number;
}

export interface OfficeFormData {
  display_name: string;
  office_type: number;
  area: number;
  price: number;
  room_count: number;
  location: number;
  floor: number;
  office_lock: number | null;
  commissioning_date: string;
  workplace_count: number;
  ceiling_height: number;
  description: string;
  finishing: OfficeFinishing;
  main_photo: string;
  additional_photos: string[];
  public: boolean;
  min_period: number;
  features: {
    [key: string]: boolean;
  };
}

const mandatoryFieldsForDescriptionGeneration: [
  keyof OfficeFormData,
  string,
][] = [
  ["location", "Локация"],
  ["display_name", "Название"],
  ["area", "Площадь"],
  ["price", "Стоимость"],
  ["room_count", "Кол-во комнат"],
  ["floor", "Этаж"],
  ["workplace_count", "Количество рабочих мест"],
];

export function OfficeForm({
  defaultValues,
  onSubmit,
  onDelete,
  isEdit = false,
  modalErrors = [],
  officeId,
}: OfficeFormProps) {
  const features = useMemo(
    () => [
      {
        label: "Технологии",
        features: [
          {
            name: "wifi",
            label: "Высокоскоростной Wi-Fi",
          },
          {
            name: "printer",
            label: "Печать и сканирование",
          },
        ],
      },
      {
        label: "Удобства",
        features: [
          {
            name: "parking",
            label: "Парковка",
          },
          {
            name: "kitchen",
            label: "Кухня",
          },
        ],
      },
      {
        label: "Помещения",
        features: [
          {
            name: "conference",
            label: "Конференц-зал",
          },
        ],
      },
      {
        label: "Услуги",
        features: [
          {
            name: "mail",
            label: "Обработка почты",
          },
        ],
      },
      {
        label: "Безопасность",
        features: [
          {
            name: "permanent_access",
            label: "Круглосуточный доступ",
          },
        ],
      },
      {
        label: "Комфорт",
        features: [
          {
            name: "conditioner",
            label: "Кондиционер",
          },
        ],
      },
      {
        label: "Напитки",
        features: [
          {
            name: "coffee",
            label: "Кофе и чай",
          },
        ],
      },
    ],
    [],
  );

  const defaultFeatures = features.reduce(
    (acc, cat) => {
      cat.features.forEach((feature) => {
        acc[feature.name] = false;
      });
      return acc;
    },
    {} as Record<string, boolean>,
  );

  const form = useForm<OfficeFormData>({
    defaultValues: {
      additional_photos: [],
      features: defaultFeatures,
      ...defaultValues,
    },
  });
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [replacePhotoIndex, setReplacePhotoIndex] = useState<number | null>(
    null,
  );
  const [isDescriptionGenerating, setIsDescriptionGenerating] = useState(false);

  const [autogenerateWorkplaceCount, setAutogenerateWorkplaceCount] =
    useState(false);

  const previewModal = useDisclosure();

  const mainPhotoUploadModal = useFileUploadModal();
  const additionalPhotosUploadModal = useFileUploadModal();

  const { CianModal, openCianModal } = useCianModal();

  useEffect(() => {
    if (modalErrors.length) {
      modalErrors.forEach(({ field, message }) => {
        form.setError(field as keyof OfficeFormData, {
          type: "manual",
          message,
        });
      });
    } else {
      form.clearErrors();
    }
  }, [modalErrors, form]);

  useEffect(() => {
    if (!defaultValues) return;

    Object.entries(defaultValues).forEach(([key, value]) => {
      if (value === undefined) return;

      if (key === "commissioning_date") {
        form.setValue("commissioning_date", normalizeDate(value as string));
      } else {
        form.setValue(key as keyof OfficeFormData, value as never);
      }
    });
  }, [defaultValues, form]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const location = form.watch("location");
  const area = form.watch("area");

  useEffect(() => {
    if (autogenerateWorkplaceCount && area) {
      form.setValue("workplace_count", Math.floor(area / 4.5));
    }
  }, [autogenerateWorkplaceCount, area, form, location]);

  const onMainPhotoUploaded = useCallback(
    (urls: string[]) => {
      form.clearErrors("main_photo");
      form.setValue("main_photo", urls[0] || "");
    },
    [form],
  );

  const onAdditionalPhotosUploaded = useCallback(
    (urls: string[]) => {
      form.clearErrors("additional_photos");
      const currentPhotos = form.getValues("additional_photos") || [];
      if (replacePhotoIndex !== null) {
        const newPhotos = [...currentPhotos];
        newPhotos[replacePhotoIndex] = urls[0] || "";
        form.setValue("additional_photos", newPhotos);
        setReplacePhotoIndex(null);
      } else {
        form.setValue("additional_photos", [...currentPhotos, ...urls]);
      }
    },
    [form, replacePhotoIndex],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const currentPhotos = form.getValues("additional_photos") || [];
        const oldIndex = currentPhotos.indexOf(active.id.toString());
        const newIndex = currentPhotos.indexOf(over.id.toString());

        form.setValue(
          "additional_photos",
          arrayMove(currentPhotos, oldIndex, newIndex),
        );
      }
    },
    [form],
  );

  const replacePhoto = useCallback(
    (index: number) => {
      form.clearErrors("additional_photos");
      setReplacePhotoIndex(index);
      additionalPhotosUploadModal.onOpen();
    },
    [additionalPhotosUploadModal, form],
  );

  const removePhoto = useCallback(
    (index: number) => {
      form.clearErrors("additional_photos");
      const currentPhotos = form.getValues("additional_photos") || [];
      const newPhotos = currentPhotos.filter((_, i) => i !== index);
      form.setValue("additional_photos", newPhotos);
    },
    [form],
  );

  const showPreview = useCallback(
    (url: string) => {
      setPreviewImage(url);
      previewModal.onOpen();
    },
    [previewModal],
  );

  const generateDescription = useCallback(() => {
    setIsDescriptionGenerating(true);

    const values = form.getValues();

    for (const [field, label] of mandatoryFieldsForDescriptionGeneration) {
      if (values[field] == null) {
        setIsDescriptionGenerating(false);
        toast.error(`Для генерации необходимо заполнить поле ${label}!`);
        return;
      }
    }

    api.offices
      .generateDescription({
        ...values,
        id: 0,
        location_display_name: "",
        additional_photos: [],
        main_photo: "",
        office_lock: values.office_lock || 0,
      })
      .then((res) => {
        form.setValue("description", res.data);
      })
      .catch((err) => {
        toast.error("Произошла ошибка при генерации описания");
        console.log(err);
      })
      .finally(() => {
        setIsDescriptionGenerating(false);
      });
  }, [form]);

  const handlePublish = useCallback(() => {
    if (officeId) {
      openCianModal(officeId);
    }
  }, [openCianModal, officeId]);

  return (
    <>
      <CianModal />
      <mainPhotoUploadModal.FileUploadModal
        onFileUploaded={onMainPhotoUploaded}
        listUploadedFiles={true}
      />
      <additionalPhotosUploadModal.FileUploadModal
        multiple={true}
        onFileUploaded={onAdditionalPhotosUploaded}
        listUploadedFiles={true}
      />

      <Modal
        isOpen={previewModal.isOpen}
        onClose={previewModal.onClose}
        size="4xl">
        <ModalContent>
          <ModalHeader>Просмотр фото</ModalHeader>
          <ModalBody>
            {previewImage && (
              <img
                src={`${S3_URL}/${previewImage}`}
                alt="Просмотр фото"
                className="w-full max-h-[50vh] rounded-xl object-contain"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <form className="w-full" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-2 md:w-1/2">
          <Controller
            control={form.control}
            name="location"
            render={({ field }) => (
              <NewLocationHandbook
                {...field}
                isFinancial={false}
                isRequired
                isInvalid={!!form.formState.errors["location"]}
                errorMessage={
                  form.formState.errors["location"]?.message?.toString() || ""
                }
                label="Локация"
                variant="bordered"
                selectedKey={field.value}
                onSelectionChange={(key) => field.onChange(key)}
                children={<></>}
              />
            )}
          />

          <Controller
            control={form.control}
            name="display_name"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                {...field}
                isInvalid={!!form.formState.errors["display_name"]}
                errorMessage={
                  form.formState.errors["display_name"]?.message?.toString() ||
                  ""
                }
                isRequired
                label="Название"
                variant="bordered"
              />
            )}
          />

          <Controller
            control={form.control}
            name="finishing"
            render={({ field }) => (
              <Autocomplete
                {...field}
                isRequired
                label="Тип отделки"
                variant="bordered"
                selectedKey={field.value}
                onSelectionChange={(key) => field.onChange(key)}>
                <AutocompleteItem key={OfficeFinishing.DONE}>
                  Чистовая
                </AutocompleteItem>
                <AutocompleteItem key={OfficeFinishing.DRAFT}>
                  Черновая
                </AutocompleteItem>
              </Autocomplete>
            )}
          />

          <Controller
            control={form.control}
            name="office_type"
            render={({ field }) => (
              <NewOfficeTypeHandbook
                isRequired
                isInvalid={!!form.formState.errors["office_type"]}
                errorMessage={
                  form.formState.errors["office_type"]?.message?.toString() ||
                  ""
                }
                label="Тип офиса"
                variant="bordered"
                selectedKey={field.value}
                onSelectionChange={(key) => field.onChange(key)}
                children={<></>}
              />
            )}
          />

          <Controller
            control={form.control}
            name="office_lock"
            render={({ field }) => (
              <CodependentLockHandbook
                {...field}
                value={String(field.value) || "0"}
                isInvalid={!!form.formState.errors["office_lock"]}
                errorMessage={
                  form.formState.errors["office_lock"]?.message?.toString() ||
                  ""
                }
                label="Замок"
                variant="bordered"
                selectedKey={String(field.value) || "0"}
                onSelectionChange={(key) => field.onChange(key)}
                deps={[String(location)]}
                children={<></>}
              />
            )}
          />

          <Controller
            control={form.control}
            name="description"
            render={({ field }) => (
              <div className="flex flex-col gap-2">
                <Textarea
                  {...field}
                  isRequired
                  value={field.value}
                  label="Описание"
                  variant="bordered"
                  isDisabled={isDescriptionGenerating}
                />
                <Button
                  color="primary"
                  variant="bordered"
                  onPress={generateDescription}
                  isDisabled={isDescriptionGenerating}
                  isLoading={isDescriptionGenerating}>
                  Сгенерировать описание
                </Button>
              </div>
            )}
          />

          <span className="text-xl font-bold mt-2 mb-2">Об объекте</span>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center">
              <span>Площадь</span>
              <span className="text-sm text-red-500 ml-1">*</span>
            </div>
            <div className="flex items-center gap-2">
              <Controller
                control={form.control}
                name="area"
                rules={{
                  max: {
                    value: 10000,
                    message: "Максимальная площадь 100.000 м²",
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value?.toString()}
                    placeholder="30"
                    variant="bordered"
                    type="number"
                    className="w-full"
                    isInvalid={!!form.formState.errors["area"]}
                    errorMessage={
                      form.formState.errors["area"]?.message?.toString() || ""
                    }
                    isRequired
                    endContent={<span className="text-gray-600">м²</span>}
                  />
                )}
              />
            </div>

            <div className="flex items-center">
              <span>Стоимость</span>
              <span className="text-sm text-red-500 ml-1">*</span>
            </div>
            <div className="flex items-center gap-2">
              <Controller
                control={form.control}
                name="price"
                rules={{
                  max: {
                    value: 1000000000,
                    message: "Максимальная стоимость 1.000.000.000 ₽",
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value?.toString()}
                    placeholder="360000"
                    variant="bordered"
                    type="number"
                    className="w-full"
                    isInvalid={!!form.formState.errors["price"]}
                    errorMessage={
                      form.formState.errors["price"]?.message?.toString() || ""
                    }
                    isRequired
                    endContent={<span className="text-gray-600">₽</span>}
                  />
                )}
              />
            </div>

            <div className="flex items-center">
              <span>Кол-во комнат</span>
              <span className="text-sm text-red-500 ml-1">*</span>
            </div>
            <div className="flex items-center">
              <Controller
                control={form.control}
                name="room_count"
                rules={{
                  max: {
                    value: 1000,
                    message: "Максимальное количество комнат 1000",
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value?.toString()}
                    placeholder="2"
                    variant="bordered"
                    type="number"
                    className="w-full"
                    isInvalid={!!form.formState.errors["room_count"]}
                    errorMessage={
                      form.formState.errors[
                        "room_count"
                      ]?.message?.toString() || ""
                    }
                    isRequired
                  />
                )}
              />
            </div>

            <div className="flex items-center">
              <span>Этаж</span>
              <span className="text-sm text-red-500 ml-1">*</span>
            </div>
            <div className="flex items-center">
              <Controller
                control={form.control}
                name="floor"
                rules={{
                  max: {
                    value: 1000,
                    message: "Максимальный этаж 1000",
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value?.toString()}
                    placeholder="3"
                    variant="bordered"
                    type="number"
                    className="w-full"
                    isInvalid={!!form.formState.errors["floor"]}
                    errorMessage={
                      form.formState.errors["floor"]?.message?.toString() || ""
                    }
                    isRequired
                  />
                )}
              />
            </div>

            <div className="flex items-center">
              <span>Дата ввода в эксплуатацию</span>
              <span className="text-sm text-red-500 ml-1">*</span>
            </div>
            <div className="flex items-center">
              <Controller
                control={form.control}
                name="commissioning_date"
                render={({ field }) => (
                  <I18nProvider locale="ru-RU">
                    <DatePicker
                      variant="bordered"
                      isRequired
                      onChange={(date) => field.onChange(date?.toString())}
                      className="w-full"
                      value={
                        field.value
                          ? parseDate(normalizeDate(field.value))
                          : undefined
                      }
                      isInvalid={!!form.formState.errors["commissioning_date"]}
                      errorMessage={
                        form.formState.errors[
                          "commissioning_date"
                        ]?.message?.toString() || ""
                      }
                    />
                  </I18nProvider>
                )}
              />
            </div>

            <div className="flex items-center">
              <span>Минимальный срок</span>
              <span className="text-sm text-red-500 ml-1">*</span>
            </div>
            <div className="flex items-center">
              <Controller
                control={form.control}
                name="min_period"
                rules={{
                  max: {
                    value: 1000,
                    message: "Максимальный срок 1000 месяцев",
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value?.toString()}
                    placeholder="1"
                    variant="bordered"
                    type="number"
                    className="w-full"
                    isInvalid={!!form.formState.errors["min_period"]}
                    errorMessage={
                      form.formState.errors[
                        "min_period"
                      ]?.message?.toString() || ""
                    }
                    isRequired
                  />
                )}
              />
            </div>

            <div className="flex items-center">
              <span>Количество рабочих мест</span>
              <span className="text-sm text-red-500 ml-1">*</span>
            </div>
            <div className="flex items-center gap-2">
              <Controller
                control={form.control}
                name="workplace_count"
                rules={{
                  max: {
                    value: 100000,
                    message: "Максимальное количество рабочих мест 100.000",
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value?.toString()}
                    placeholder="10"
                    variant="bordered"
                    type="number"
                    className="w-full"
                    isDisabled={autogenerateWorkplaceCount}
                    isInvalid={!!form.formState.errors["workplace_count"]}
                    errorMessage={
                      form.formState.errors[
                        "workplace_count"
                      ]?.message?.toString() || ""
                    }
                    isRequired
                  />
                )}
              />
              <Tooltip content="Автоматический рассчёт рабочих мест происходит на основе площади офиса.">
                <Checkbox
                  isSelected={autogenerateWorkplaceCount}
                  onValueChange={setAutogenerateWorkplaceCount}>
                  Авто
                </Checkbox>
              </Tooltip>
            </div>

            <div className="flex items-center">
              <span>Высота потолка</span>
              <span className="text-sm text-red-500 ml-1">*</span>
            </div>
            <div className="flex items-center">
              <Controller
                control={form.control}
                name="ceiling_height"
                rules={{
                  max: {
                    value: 1000,
                    message: "Максимальная высота потолка 1000 м",
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value?.toString()}
                    placeholder="3.2"
                    variant="bordered"
                    type="number"
                    className="w-full"
                    isInvalid={!!form.formState.errors["ceiling_height"]}
                    errorMessage={
                      form.formState.errors[
                        "ceiling_height"
                      ]?.message?.toString() || ""
                    }
                    isRequired
                  />
                )}
              />
            </div>

            <div className="flex items-center">
              <span>Виден на маркетплейсе</span>
            </div>
            <div className="flex items-center">
              <Controller
                control={form.control}
                name="public"
                render={({ field }) => (
                  <Checkbox
                    isSelected={field.value}
                    onValueChange={field.onChange}>
                    <span>Виден на маркетплейсе</span>
                  </Checkbox>
                )}
              />
            </div>
          </div>

          <span className="text-xl font-bold mt-2 mb-2">Фотографии</span>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <span className="text-sm text-gray-500">Главное фото</span>
              {form.watch("main_photo") ? (
                <div className="flex flex-col gap-2">
                  <img
                    src={`${S3_URL}/${form.watch("main_photo")}`}
                    alt="Главное фото"
                    className="w-full max-h-[300px] rounded-xl object-contain"
                  />
                  <div className="flex gap-2">
                    <Button
                      color="primary"
                      variant="bordered"
                      onPress={mainPhotoUploadModal.onOpen}
                      startContent={<FaUpload />}>
                      Заменить фото
                    </Button>
                    <Button
                      color="danger"
                      variant="light"
                      onPress={() => form.setValue("main_photo", "")}
                      startContent={<FaTimesIcon />}>
                      Удалить
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <Button
                    color="primary"
                    variant="bordered"
                    onPress={mainPhotoUploadModal.onOpen}
                    startContent={<FaUpload />}>
                    Загрузить фото
                  </Button>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm text-gray-500">
                Дополнительные фото (до 10)
              </span>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}>
                <div className="grid grid-cols-5 gap-2">
                  <SortableContext
                    items={form.watch("additional_photos") || []}
                    strategy={verticalListSortingStrategy}>
                    {(form.watch("additional_photos") || []).map(
                      (photo, index) => (
                        <SortableItem key={photo} id={photo}>
                          <div className="relative aspect-square">
                            <img
                              src={`${S3_URL}/${photo}`}
                              alt={`Дополнительное фото ${index + 1}`}
                              className="w-full h-full rounded-xl object-cover cursor-pointer"
                              onClick={() => showPreview(photo)}
                            />
                            <div className="absolute top-2 right-2 flex gap-1">
                              <Button
                                isIconOnly
                                size="sm"
                                color="primary"
                                onPress={() => replacePhoto(index)}>
                                <FaUpload />
                              </Button>
                              <Button
                                isIconOnly
                                size="sm"
                                color="danger"
                                onPress={() => removePhoto(index)}>
                                <FaTimesIcon />
                              </Button>
                            </div>
                          </div>
                        </SortableItem>
                      ),
                    )}
                  </SortableContext>
                </div>
              </DndContext>

              {(form.watch("additional_photos") || []).length < 10 && (
                <Button
                  color="primary"
                  variant="bordered"
                  onPress={additionalPhotosUploadModal.onOpen}
                  startContent={<FaUpload />}>
                  Добавить фото
                </Button>
              )}

              {form.formState.errors["additional_photos"] && (
                <span className="text-red-500">
                  {form.formState.errors[
                    "additional_photos"
                  ]?.message?.toString() || ""}
                </span>
              )}
            </div>
          </div>

          <span className="text-xl font-bold mt-2 mb-2">Удобства</span>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {features.map((feature) => (
              <div key={feature.label} className="flex flex-col gap-2 mb-2">
                <span className="text-zinc-500 mb-2">{feature.label}</span>
                {feature.features.map((feature) => (
                  <Controller
                    key={feature.name}
                    control={form.control}
                    name={`features.${feature.name}`}
                    render={({ field }) => (
                      <Checkbox
                        isSelected={field.value}
                        onValueChange={field.onChange}>
                        <span>{feature.label}</span>
                      </Checkbox>
                    )}
                  />
                ))}
              </div>
            ))}
          </div>

          <div className="flex gap-2 justify-end mt-4">
            {isEdit && officeId && (
              <Button
                color="primary"
                variant="bordered"
                onPress={handlePublish}>
                Отправить в ЦИАН
              </Button>
            )}
            {isEdit && onDelete && (
              <Button color="danger" variant="light" onPress={onDelete}>
                Удалить офис
              </Button>
            )}
            <Button
              color="primary"
              type="submit"
              isDisabled={form.formState.isSubmitting}>
              {isEdit ? "Сохранить" : "Создать"}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
